import { memo } from 'react';
import { ViewProps, ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
  useSharedValue,
} from 'react-native-reanimated';
import { useEffect } from 'react';
import Box from '../Box/Box';
import useStyle from '../../hooks/useStyle';
import { Styles } from '../../types/styles';
import { BoxProps } from '../Box/Box.types';
import { SpacingTokens } from '../../theme/index.types';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export type TooltipProps = ViewProps & {
  children?: React.ReactNode;
  styles?: Styles<ViewStyle>;
  position?: TooltipPosition;
  isVisible?: boolean;
  offset?: SpacingTokens;
  duration?: number;
  delay?: number;
  autoHide?: boolean;
  showAnimation?: boolean;
  variant?: BoxProps['variant'];
  zIndex?: number;
  onShow?: () => void;
  onHide?: () => void;
};

const TOOLTIP_DEFAULT_STYLES: Styles<ViewStyle> = {
  position: 'absolute',
  left: 'space-1400',
  padding: 'space-100',
  borderRadius: 'radius-100',
};

const getPositionStyle = (position: TooltipPosition, offset: SpacingTokens) => {
  const positionStyles: Record<TooltipPosition, Styles<ViewStyle>> = {
    top: {
      bottom: 'space-0',
      marginBottom: offset,
      alignItems: 'center',
    },
    bottom: {
      top: 'space-0',
      marginTop: offset,
      alignItems: 'center',
    },
    left: {
      right: 'space-0',
      marginRight: offset,
      alignItems: 'flex-end',
    },
    right: {
      left: 'space-0',
      marginLeft: offset,
      alignItems: 'flex-start',
    },
  };
  return positionStyles[position];
};

function Tooltip({
  children,
  styles,
  isVisible = true,
  position = 'top',
  offset = 'space-0',
  duration = 400,
  delay = 3200,
  autoHide = true,
  showAnimation = true,
  variant = 'gradient',
  zIndex = 1000,
  onShow,
  onHide,
  ...props
}: TooltipProps) {
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(0);

  const positionStyle = getPositionStyle(position, offset);
  const tooltipStyle = useStyle<ViewStyle>({
    ...TOOLTIP_DEFAULT_STYLES,
    ...positionStyle,
    zIndex,
    ...styles,
  });

  useEffect(() => {
    if (!isVisible) {
      opacity.value = withTiming(0, { duration });
      translateY.value = withTiming(0, { duration });
      onHide?.();
      return;
    }

    // Show animation
    opacity.value = withTiming(1, { duration });
    translateY.value = withTiming(position === 'top' ? -20 : 20, { duration });
    onShow?.();

    // Auto-hide animation
    if (autoHide) {
      const hideTimeout = setTimeout(() => {
        opacity.value = withTiming(0, { duration });
        translateY.value = withTiming(0, { duration });
        onHide?.();
      }, delay);

      return () => clearTimeout(hideTimeout);
    }
  }, [
    isVisible,
    position,
    duration,
    delay,
    autoHide,
    opacity,
    translateY,
    onShow,
    onHide,
  ]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: showAnimation ? opacity.value : isVisible ? 1 : 0,
    transform: showAnimation
      ? [
          { translateY: translateY.value },
          { translateX: position === 'top' || position === 'bottom' ? -50 : 0 },
        ]
      : [],
  }));

  return (
    <Animated.View style={[tooltipStyle, animatedStyle]} {...props}>
      <Box variant={variant} padding="space-100" borderRadius="radius-100">
        {children}
      </Box>
    </Animated.View>
  );
}

export default memo(Tooltip);
